import { Form, FormRef } from 'common/form';
import { useEffect, useRef, useState } from 'react';
import {
  CalendarMeetingTemplateFormData,
  calendarMeetingTemplateSchema,
  MeetingLocation
} from 'modules/calendar/constants/calendarMeetingTemplateSchema';
import {
  getCalendarMeetingTemplateGroups,
  getCalendarMeetingTemplateFields
} from 'modules/calendar/constants/calendarMeetingTemplateFields';
import { UseUserCalendarReturnData } from 'modules/calendar/hooks/useUserCalendar';
import { AvailabilityMethod } from 'generated-graphql/graphql';
import { getParticipantStats } from 'modules/calendar/utils/getParticipantStats';
import { CalendarMeetingTemplateFormSummary } from './CalendarMeetingTemplateFormSummary';
import { ToastType, useToast } from 'services/toast';
import { FailedMessageWithHref } from 'common/ui';
import { Alert, Box, SxProps } from '@mui/material';
import { Header } from 'common/ui/Header';
import { useNavigate } from 'react-router-dom';

const layoutContainerSx: SxProps = {
  pt: 0,
  maxWidth: 800
};

type Props = {
  breadcrumbs: { name: string; path: string }[];
  currentBreadcrumb: string;
  title: string;
  subtitle?: string;
  type: 'create' | 'edit' | 'preview';
  defaultFormData?: CalendarMeetingTemplateFormData | null;
  userCalendars: UseUserCalendarReturnData;
  onSave: (
    data: CalendarMeetingTemplateFormData,
    dirtyFields?: string[]
  ) => void;
  readOnlyReason?: string | null;
  unresolvedMembers?: { email: string; name: string }[];
};

export const UpsertCalendarMeetingTemplateForm: React.FC<Props> = ({
  breadcrumbs,
  currentBreadcrumb,
  title,
  subtitle,
  type,
  defaultFormData,
  userCalendars,
  onSave,
  readOnlyReason,
  unresolvedMembers
}) => {
  const formRef = useRef<FormRef<CalendarMeetingTemplateFormData>>(null);
  const [formData, setFormData] = useState<
    CalendarMeetingTemplateFormData | null | undefined
  >(defaultFormData);
  const [ownerId, setOwnerId] = useState<string | null>(
    formData?.owner || null
  );
  const [isLocationInPersonSelected, setIsLocationInPersonSelected] =
    useState(false);
  const [participantCount, setParticipantCount] = useState(
    () =>
      getParticipantStats(
        formData?.owner,
        formData?.participants,
        userCalendars
      ).participantCount
  );
  const [hasVirtualParticipant, setHasVirtualParticipant] = useState(
    () =>
      getParticipantStats(
        formData?.owner,
        formData?.participants,
        userCalendars
      ).hasVirtualParticipant
  );
  const [isRoundRobinSelected, setIsRoundRobinSelected] = useState(
    () =>
      (formData?.schedulingMethod ?? AvailabilityMethod.Collective) !==
      AvailabilityMethod.Collective
  );
  const [dirtyFields, setDirtyFields] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(
    !readOnlyReason && (type === 'create' || type === 'edit')
  );
  const { showToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!formRef?.current) {
      return;
    }

    const subscription = formRef.current.watch((value: any, { name }: any) => {
      if (name === 'owner') {
        setOwnerId(value.owner);
      }

      if (name === 'location') {
        setIsLocationInPersonSelected(
          value.location === MeetingLocation.InPerson
        );
      }

      if (name === 'schedulingMethod') {
        const isRoundRobin =
          value.schedulingMethod !== AvailabilityMethod.Collective;
        setIsRoundRobinSelected(isRoundRobin);

        const currentOwner: string | undefined = value.owner;
        const currentParticipants: string[] = value.participants ?? [];

        // Round robin has no fixed organizer and collective needs one, so
        // switching moves them between the two values.
        let nextOwner = currentOwner;
        let nextParticipants = currentParticipants;

        if (isRoundRobin && currentOwner) {
          nextOwner = '';
          nextParticipants = [currentOwner, ...currentParticipants];
        } else if (
          !isRoundRobin &&
          !currentOwner &&
          currentParticipants.length > 0
        ) {
          nextOwner = currentParticipants[0];
          nextParticipants = currentParticipants.slice(1);
        }

        if (nextOwner !== currentOwner) {
          formRef.current?.setValue('owner', nextOwner);
        }

        // The panel rule differs by method, so an existing error is stale.
        formRef.current?.setValue('participants', nextParticipants, {
          shouldValidate: true
        });
      }

      // Array edits surface as `participants` or `participants.N`.
      if (name === 'owner' || (name ?? '').startsWith('participants')) {
        const stats = getParticipantStats(
          value.owner,
          value.participants,
          userCalendars
        );
        setParticipantCount(stats.participantCount);
        setHasVirtualParticipant(stats.hasVirtualParticipant);
      }
    });

    return () => subscription.unsubscribe();
  }, [formRef?.current?.watch, isEditing, userCalendars]);

  useEffect(() => {
    if (formData?.location === MeetingLocation.InPerson) {
      setIsLocationInPersonSelected(true);
    }
  }, [formData?.location]);

  const handleOrganizerChange = (id: string) => {
    const form = formRef.current;

    if (!form) {
      return;
    }

    form.setValue('owner', id, { shouldValidate: true });
    // `owner` is hidden, so the panel's rules report on `participants` — without
    // revalidating it, "Choose an organiser." stays on screen after one is chosen.
    form.setValue('participants', form.getValues().participants ?? [], {
      shouldValidate: true
    });
  };

  const handleSave = () => {
    if (!formData) {
      showToast({
        title: 'You need to fill out the form',
        message: (
          <FailedMessageWithHref
            failedMessage="Try edited this meeting template again."
            emailSubject="Failed to edit meeting template"
          />
        ),
        type: ToastType.Error
      });

      return;
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
    onSave(formData, dirtyFields);
  };

  const handleBack = () => {
    if (type === 'create' && !isEditing) {
      return setIsEditing(true);
    } else if (type === 'edit' && isEditing) {
      return setIsEditing(false);
    }
    return navigate(-1);
  };

  return (
    <Box sx={layoutContainerSx}>
      <Header
        breadcrumbs={breadcrumbs}
        currentBreadcrumb={currentBreadcrumb}
        title={title}
        subtitle={subtitle}
        onBack={handleBack}
      />
      {readOnlyReason && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {readOnlyReason}
        </Alert>
      )}
      {isEditing && (
        <Form
          ref={formRef}
          defaultValues={formData}
          schema={calendarMeetingTemplateSchema}
          fields={getCalendarMeetingTemplateFields(type, userCalendars, {
            isLocationInPersonSelected,
            ownerId,
            participantCount,
            hasVirtualParticipant,
            isRoundRobinSelected,
            unresolvedMembers,
            onOrganizerChange: handleOrganizerChange
          })}
          context={{
            userCalendars,
            unresolvedMemberCount: unresolvedMembers?.length ?? 0
          }}
          groups={getCalendarMeetingTemplateGroups({ isRoundRobinSelected })}
          submitButtonProps={{
            label: type === 'create' ? 'Create template' : 'Save changes'
          }}
          sx={{
            width: '700px',
            mb: 3
          }}
          onSubmit={(data: CalendarMeetingTemplateFormData) => {
            // The form remounts per pass with the last values as its defaults, so
            // a later pass reports nothing dirty. Accumulate or lose earlier edits.
            setDirtyFields((previous) => [
              ...new Set([
                ...previous,
                ...(formRef.current?.getDirtyFields() ?? [])
              ])
            ]);
            setIsEditing(false);
            setFormData(data);
            window.scrollTo({ top: 0, behavior: 'auto' });
          }}
        />
      )}
      {!isEditing && formData && userCalendars && (
        <CalendarMeetingTemplateFormSummary
          formData={formData}
          userCalendars={userCalendars}
          isReadOnly={!!readOnlyReason}
          hideHeading={type === 'preview' || !!readOnlyReason}
          onEditPressed={() => {
            setIsEditing(true);
            window.scrollTo({ top: 0, behavior: 'auto' });
          }}
          onSavePressed={handleSave}
        />
      )}
    </Box>
  );
};
